export const SERVICE_UNAVAILABLE_ERROR = "service_unavailable";
export const MAX_SERVICE_UNAVAILABLE_RETRIES = 3;
export const DEFAULT_RETRY_AFTER_MS = 5_000;
export const MAX_RETRY_AFTER_MS = 30_000;

export type ServiceUnavailable = {
  service?: string;
  requestId?: string;
  retryAfterMs: number;
};

export type ServiceUnavailableError = Error & {
  status: 503;
  code: typeof SERVICE_UNAVAILABLE_ERROR;
  service?: string;
};

export function parseRetryAfterMs(header: string | null, now: number = Date.now()): number {
  const value = header?.trim() ?? "";
  if (/^\d+$/.test(value)) return Math.min(Number(value) * 1000, MAX_RETRY_AFTER_MS);
  const date = /GMT$/.test(value) ? Date.parse(value) : Number.NaN;
  if (Number.isNaN(date)) return DEFAULT_RETRY_AFTER_MS;
  return Math.min(Math.max(date - now, 0), MAX_RETRY_AFTER_MS);
}

export function isServiceUnavailableBody(
  body: unknown,
): body is { error: typeof SERVICE_UNAVAILABLE_ERROR; service?: unknown; request_id?: unknown } {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { error?: unknown }).error === SERVICE_UNAVAILABLE_ERROR
  );
}

export async function readServiceUnavailable(
  response: Response,
): Promise<ServiceUnavailable | null> {
  if (response.status !== 503) return null;
  let body: unknown;
  try {
    body = await response.clone().json();
  } catch {
    return null;
  }
  if (!isServiceUnavailableBody(body)) return null;
  return {
    ...(typeof body.service === "string" && body.service ? { service: body.service } : {}),
    ...(typeof body.request_id === "string" ? { requestId: body.request_id } : {}),
    retryAfterMs: parseRetryAfterMs(response.headers.get("Retry-After")),
  };
}

export function waitForRetry(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function isServiceUnavailableError(error: unknown): error is ServiceUnavailableError {
  return (
    error instanceof Error &&
    (error as { status?: unknown }).status === 503 &&
    (error as { code?: unknown }).code === SERVICE_UNAVAILABLE_ERROR
  );
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return !isServiceUnavailableError(error) && failureCount < 1;
}
